import axios from "axios";
import config from "./index";
import { publishWeb } from "../services/navigate.service";

// One refresh per page: the host and every module bundle share the in-flight promise through window,
// because the refresh token rotates on use and a second concurrent call would be refused.
const REFRESH_SLOT = "__finexTokenRefresh";

const rawClient = axios.create({ baseURL: config.api.baseUrl, timeout: config.api.timeout });

export const refreshAccessToken = (failedAccessToken?: string): Promise<string> => {
    const shared = window as any;
    if (shared[REFRESH_SLOT]) return shared[REFRESH_SLOT];

    // A 401 that arrives after another refresh already finished only needs the newer token.
    const current = localStorage.getItem("accessToken");
    if (failedAccessToken && current && current !== failedAccessToken) return Promise.resolve(current);

    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) return Promise.reject(new Error("No refresh token"));

    const attempt: Promise<string> = rawClient
        .post(`/api/auth/refresh-token`, { refreshToken })
        .then((response) => {
            if (!response.data?.success) throw new Error("Refresh rejected");
            localStorage.setItem("accessToken", response.data.accessToken);
            localStorage.setItem("refreshToken", response.data.refreshToken);
            return response.data.accessToken as string;
        })
        .catch((error) => {
            // Only a 401 is a verdict on the session; anything else just fails this request.
            if (error?.response?.status === 401) {
                // Another tab may have rotated while this call was in flight: its pair is already stored.
                const storedRefresh = localStorage.getItem("refreshToken");
                const storedAccess = localStorage.getItem("accessToken");
                if (storedRefresh && storedRefresh !== refreshToken && storedAccess) return storedAccess;
                publishWeb("refreshTokenFailed", {
                    title: "Your Session Has Ended",
                    desc: "Your session has expired. Please log in again.",
                });
            }
            throw error;
        })
        .finally(() => {
            if (shared[REFRESH_SLOT] === attempt) delete shared[REFRESH_SLOT];
        });
    shared[REFRESH_SLOT] = attempt;
    return attempt;
};
